import React from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine,
    Legend,
    LabelList,
} from "recharts";
import {
    construirPuntosGrafica,
    esNulo,
} from "../helpers/puntosGraficaCierres";
import {
    COLOR_POR_TIPO,
    TIPO_LABORATORIO,
    TIPO_LIQUIDACION,
    TITULOS_POR_TIPO,
} from "../helpers/tiposReporteCierres";
import { TickPeriodoCierre } from "./TickPeriodoCierre";

// Una línea por cada forma de medir lo enviado a producción; los nombres son los
// de la última columna de cada versión de la tabla
const SERIES = [
    {
        dataKey: "porcentaje_variacion_laboratorio",
        nombre: TITULOS_POR_TIPO[TIPO_LABORATORIO].porcentaje,
        color: COLOR_POR_TIPO[TIPO_LABORATORIO],
    },
    {
        dataKey: "porcentaje_variacion_liquidacion",
        nombre: TITULOS_POR_TIPO[TIPO_LIQUIDACION].porcentaje,
        color: COLOR_POR_TIPO[TIPO_LIQUIDACION],
    },
    {
        dataKey: "porcentaje_variacion_tolva",
        nombre: "% de variación entre tolva y producción",
        color: "#8E24AA",
    },
];

// El eje se queda en ±2% mientras los datos quepan y solo se estira cuando
// alguna variación se sale, igual que en la gráfica de traslados
const LIMITE_MINIMO_EJE = 2;

const calcularDominioEje = (data) => {
    const limite = data.reduce(
        (mayor, punto) =>
            SERIES.reduce((mayorDelPunto, serie) => {
                const valor = punto[serie.dataKey];

                return esNulo(valor)
                    ? mayorDelPunto
                    : Math.max(mayorDelPunto, Math.abs(valor));
            }, mayor),
        LIMITE_MINIMO_EJE,
    );

    const limiteRedondeado = Math.ceil(limite);

    return [-limiteRedondeado, limiteRedondeado];
};

const formatearPorcentaje = (valor) => valor + "%";

const formatearEtiquetaPorcentaje = (valor) => valor.toFixed(2) + "%";

// Distancia de la etiqueta al centro del punto, que con su borde mide 8 px de
// radio
const SEPARACION_ETIQUETA_PUNTO = 14;

// La etiqueta se corre a la derecha del punto, como en la gráfica del cliente
const DESPLAZAMIENTO_ETIQUETA_DERECHA = 20;

// Variación de laboratorio, liquidación y tolva de cada cierre del rango. Los
// cierres sin variación de laboratorio o de liquidación quedan unidos por la
// línea, como en la gráfica de traslados
export const GraficaVariacionCierres = ({ hook }) => {
    const { graficaVariacion } = hook;

    if (!graficaVariacion.length) {
        return null;
    }

    const data = construirPuntosGrafica(graficaVariacion, SERIES);

    return (
        <div className="reporte-cierres-mensuales__grafica">
            <ResponsiveContainer>
                <LineChart
                    data={data}
                    margin={{
                        top: 30,
                        right: 50,
                        left: 20,
                        bottom: 10,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                        dataKey="periodo"
                        interval={0}
                        height={45}
                        tick={<TickPeriodoCierre />}
                    />

                    <YAxis
                        domain={calcularDominioEje(data)}
                        tickFormatter={formatearPorcentaje}
                    />

                    <ReferenceLine y={0} stroke="#000" strokeWidth={2} />

                    <Tooltip formatter={formatearPorcentaje} />

                    <Legend />

                    {SERIES.map((serie) => (
                        <Line
                            key={serie.dataKey}
                            type="linear"
                            dataKey={serie.dataKey}
                            stroke={serie.color}
                            strokeWidth={4}
                            dot={{ r: 6 }}
                            activeDot={{ r: 7 }}
                            name={serie.nombre}
                            connectNulls
                        >
                            <LabelList
                                position="top"
                                offset={SEPARACION_ETIQUETA_PUNTO}
                                dx={DESPLAZAMIENTO_ETIQUETA_DERECHA}
                                formatter={formatearEtiquetaPorcentaje}
                                fill={serie.color}
                                fontSize={12}
                            />
                        </Line>
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};
